import React, { useState, useEffect } from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import moment, { Moment } from "moment-timezone";
import Popover from "components/Popover/Popover";
import Button from "components/Buttons/Button";
import { DATE_PICKER_DATE_FORMAT } from "Consts";
import { CustomDatePicker } from "./CustomDatePicker";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import globalFilterTypes from "reduxStore/globalFilter/globalFilter.types";
import { isNullOrUndefined } from "utils";

const WisdomDatePicker: React.FC<any> = ({
  onApply,
  onCancel,
  isAutoRefresh,
  dateRange,
  noBorder = false,
}: any) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isSilencingWindows = location.pathname.includes("silencing_windows");
  const dashboards = ["/dashboards/host", "/dashboards/instance", "/dashboards/database"];
  const isDashboards = dashboards?.includes(location.pathname);

  const initialStartDate = isSilencingWindows
    ? moment().subtract(30, "days")
    : moment().subtract(isDashboards ? 30 : 7, "days");
  const initialEndDate = isSilencingWindows ? moment().add(30, "days") : moment();

  const [isOpen, setOpen] = useState(false);
  const [start, setStart] = useState<Moment>(initialStartDate);
  const [end, setEnd] = useState<Moment>(initialEndDate);
  const [quickTime, setQuickTime] = useState<string | number>("");
  const { DatePickerParams, QuickTime } = useSelector(globalFilterSelector, shallowEqual);

  useEffect(() => {
    if (
      ![DatePickerParams.start, DatePickerParams.end].includes("now") &&
      !isSilencingWindows &&
      !isDashboards
    ) {
      setStart(moment(DatePickerParams.start));
      setEnd(moment(DatePickerParams.end));
    } else {
      setStart(initialStartDate);
      setEnd(initialEndDate);
    }

    setQuickTime("");
    dispatch({
      payload: "",
      type: globalFilterTypes.UPDATE_QUICK_TIME,
    });
  }, [DatePickerParams]);

  useEffect(() => {
    if (isNullOrUndefined(dateRange)) {
      return;
    }

    if ([dateRange.start, dateRange.end].includes("now")) {
      setStart(initialStartDate);
      setEnd(initialEndDate);

      setQuickTime("");
      dispatch({
        payload: "",
        type: globalFilterTypes.UPDATE_QUICK_TIME,
      });
    } else {
      setStart(moment(dateRange?.start));
      setEnd(moment(dateRange?.end));
    }
  }, [dateRange]);

  useEffect(() => {
    setQuickTime(QuickTime || "");
  }, [QuickTime]);

  const handleApply = (start: any, end: any) => {
    onApply({
      start: start.format(DATE_PICKER_DATE_FORMAT),
      end: end.format(DATE_PICKER_DATE_FORMAT),
    });
    setStart(start);
    setEnd(end);
    setOpen(false);
    dispatch({
      payload: quickTime,
      type: globalFilterTypes.UPDATE_QUICK_TIME,
    });
  };

  const handleCancel = () => {
    if (onCancel) onCancel(null);
    setOpen(false);
  };

  const handleReset = () => {
    onApply({
      start: initialStartDate.format(DATE_PICKER_DATE_FORMAT),
      end: moment(initialEndDate).format(DATE_PICKER_DATE_FORMAT),
    });
    setStart(initialStartDate);
    setEnd(initialEndDate);

    setQuickTime("");
    dispatch({
      payload: "",
      type: globalFilterTypes.UPDATE_QUICK_TIME,
    });
    setOpen(false);
  };

  useEffect(() => {
    return () => {
      if (location.pathname.includes("performance_overview")) {
        dispatch({
          payload: {
            DatePickerParams: {
              start: initialStartDate.format(DATE_PICKER_DATE_FORMAT),
              end: moment(initialEndDate).format(DATE_PICKER_DATE_FORMAT),
            },
          },
          type: globalFilterTypes.SET_CURRENT_FILTER,
        });

        setQuickTime("");
        dispatch({
          payload: "",
          type: globalFilterTypes.UPDATE_QUICK_TIME,
        });
      }
    };
  }, [location.pathname]);

  return (
    <Popover
      button={
        <Button
          iconType={"calendar"}
          style={{
            color: isAutoRefresh ? "#798eaf" : "var(--text)",
            border: noBorder ? "transparent" : "1px solid var(--border-color)",
            backgroundColor: isAutoRefresh
              ? "var(--app-background) !important"
              : "var(--card-background) !important",
            fontWeight: 500,
            fontSize: "1rem",
          }}
          text={
            isAutoRefresh
              ? `Last 1 hour`
              : `${start.format("MMM D YYYY")} - ${end.format("MMM D YYYY")}`
          }
          type={"secondary"}
          onClick={(e) => {
            e.stopPropagation();
            if (!isAutoRefresh) {
              setOpen(!isOpen);
            }
          }}
        />
      }
      onClose={() => {
        setOpen(false);
      }}
      noCloseOnScroll
      isOpen={isOpen}
      children={
        <CustomDatePicker
          onApply={handleApply}
          onCancel={handleCancel}
          onReset={handleReset}
          initialStartDate={start}
          initialEndDate={end}
          quickTime={quickTime}
          setQuickTime={setQuickTime}
          isSilencingWindows={isSilencingWindows}
        />
      }
    />
  );
};

export default WisdomDatePicker;
